import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid, Cell } from "recharts";
import { TrendingUp, Wallet, Megaphone, FileSignature, KeyRound } from "lucide-react";
import { api, fmtNum } from "@/lib/api";

const tip = { contentStyle: { background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#F8FAFC", direction: "rtl" } };

export default function Reports() {
  const [r, setR] = useState(null);
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/reports").then((x) => setR(x.data)).catch(() => {});
    api.get("/stats").then((x) => setS(x.data)).catch(() => {});
  }, []);
  if (!r || !s) return <div className="p-10 text-slate-500">جارٍ تحميل التقارير...</div>;

  const kpis = [
    { icon: Wallet, label: "إجمالي الأرصدة", value: fmtNum(s.finance.balance), c: "#F59E0B" },
    { icon: TrendingUp, label: "صافي التدفق", value: fmtNum(s.finance.inflow - s.finance.outflow), c: "#10B981" },
    { icon: Megaphone, label: "ROI الإعلانات", value: `${fmtNum(s.ads.roi, 1)}%`, c: "#A855F7" },
    { icon: FileSignature, label: "مبيعات المصانع الموقّعة", value: `${fmtNum(r.sales.total)} (${r.sales.signed})`, c: "#EF4444" },
    { icon: KeyRound, label: "مفاتيح مربوطة", value: fmtNum(s.keys), c: "#00F0FF" },
  ];

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="reports-page">
      <div>
        <p className="text-[11px] font-mono tracking-widest text-slate-500">الإدارة العامة</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-50">التقارير والإحصائيات</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="kpi fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`kpi-${i}`}>
            <div className="absolute -top-10 -left-10 h-28 w-28 rounded-full blur-2xl opacity-20" style={{ background: k.c }} />
            <k.icon size={18} style={{ color: k.c }} />
            <p className="text-xs text-slate-400 mt-3">{k.label}</p>
            <p className="font-mono text-xl text-slate-50 mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-5" data-testid="chart-factories">
          <p className="font-semibold text-slate-100 mb-4">حجم الموارد لكل مصنع</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={r.factories}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} orientation="right" />
              <Tooltip {...tip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="total" name="العناصر" radius={[6, 6, 0, 0]}>
                {r.factories.map((f) => <Cell key={f.factory} fill={f.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel p-5" data-testid="chart-activity">
          <p className="font-semibold text-slate-100 mb-4">النشاط خلال 14 يوماً (المالك مقابل الوكيل)</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={r.activity}>
              <defs>
                <linearGradient id="gO" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94A3B8" stopOpacity={0.4} /><stop offset="100%" stopColor="#94A3B8" stopOpacity={0} /></linearGradient>
                <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity={0.4} /><stop offset="100%" stopColor="#00F0FF" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="day" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} orientation="right" allowDecimals={false} />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="owner" name="المالك" stroke="#94A3B8" fill="url(#gO)" strokeWidth={2} />
              <Area type="monotone" dataKey="agent" name="الوكيل" stroke="#00F0FF" fill="url(#gA)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="panel p-5" data-testid="chart-cashflow">
          <p className="font-semibold text-slate-100 mb-4">التدفق المالي الشهري</p>
          {r.cashflow.length === 0 ? <p className="text-sm text-slate-500 py-16 text-center">لا معاملات مسجلة بعد</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={r.cashflow}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} orientation="right" />
                <Tooltip {...tip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="in" name="وارد" fill="#10B981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="out" name="صادر" fill="#EF4444" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="panel p-5" data-testid="table-campaigns">
          <p className="font-semibold text-slate-100 mb-4">أداء الحملات الإعلانية</p>
          {r.campaigns.length === 0 ? <p className="text-sm text-slate-500 py-16 text-center">لا حملات بعد</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] font-mono text-slate-500 text-right"><th className="pb-2">الحملة</th><th className="pb-2">المنصة</th><th className="pb-2">المصروف</th><th className="pb-2">الإيراد</th><th className="pb-2">ROI</th></tr></thead>
              <tbody>
                {r.campaigns.map((c, i) => {
                  const roi = c.spent ? ((c.revenue - c.spent) / c.spent) * 100 : 0;
                  return (
                    <tr key={i} className="border-t border-white/[0.05]">
                      <td className="py-2 text-slate-200">{c.name}</td><td className="py-2 text-slate-400">{c.platform}</td>
                      <td className="py-2 font-mono">{fmtNum(c.spent)}</td><td className="py-2 font-mono">{fmtNum(c.revenue)}</td>
                      <td className={`py-2 font-mono ${roi >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtNum(roi, 1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
